import type { Resource } from '@efdigital/ll-addon-data';
import { DataTypeContent } from '@efdigital/ll-addon-data/data-types';
import { cacheKeyFor, type Store } from '@warp-drive/core';
import {
  createRecord,
  deleteRecord,
  query,
} from '@warp-drive/utilities/json-api';

import {
  filterText,
  include,
  jsonApiOptions,
  page,
  type PageOptions,
  sort,
} from '../../json-api.ts';
import {
  type CoachingSharedResource,
  DataTypeCoachingSharedResource,
  type SharedResourceItem,
} from '../../types/coaching-shared-resource.ts';

const MAX_SHARES_PER_PARTNERSHIP = 100;

/**
 * Shares on one coaching partnership, newest first, with the learning resource
 * included so the list can show title / abstract without a second round-trip.
 *
 * ll-api admits the two parties of a chemistry/chosen partnership. Shares on a
 * reassigned pairing are hidden.
 */
export function queryCoachingSharedResourcesByPartnership(
  coachingPartnershipId: string
) {
  return query<CoachingSharedResource>(
    DataTypeCoachingSharedResource,
    {
      'filter[coachingPartnership.id]': coachingPartnershipId,
      ...page({ offset: 0, limit: MAX_SHARES_PER_PARTNERSHIP }),
      ...include(['resource']),
      'sort[]': '-createdAt',
    },
    jsonApiOptions
  );
}

export function createCoachingSharedResource(
  share: CoachingSharedResource,
  store: Store
) {
  const init = createRecord(share, {
    ...jsonApiOptions,
  });

  init.body = JSON.stringify({
    data: store.cache.peek(cacheKeyFor(share)),
  });

  init.headers.set('Content-Type', 'application/vnd.api+json');
  return init;
}

export function deleteCoachingSharedResource(share: CoachingSharedResource) {
  return deleteRecord(share, jsonApiOptions);
}

/**
 * Published catalog search for the share modal.
 *
 * Coaches with `read-my:coachees` skip subscription redaction on `resources`,
 * so this returns full published bodies rather than teasers. Drafts stay out
 * (a separate collection) and archived is excluded server-side by default.
 *
 * `secondary` and `hidden` are NOT filtered server-side by default - ll-api
 * only auto-excludes `archived`, so anything demoted to secondary or marked
 * hidden must be filtered explicitly here, the same way the Leadership Live
 * site itself does. The 2026 content audit moved ~1,700 resources to
 * secondary specifically so they'd sit outside the primary library; without
 * these two filters this search would surface them anyway.
 */
export function queryCatalogResources(text: string, paging: PageOptions) {
  return query<Resource>(
    DataTypeContent.Resource,
    {
      ...filterText(text),
      'filter[secondary]': 'false',
      'filter[hidden]': 'false',
      ...page(paging),
      ...sort('-rank'),
    },
    jsonApiOptions
  );
}

async function resolveShareResource(
  share: CoachingSharedResource
): Promise<Resource | null> {
  try {
    return (await share.resource) ?? null;
  } catch {
    return null;
  }
}

export async function loadSharesForPartnership(
  store: Store,
  coachingPartnershipId: string
): Promise<SharedResourceItem[]> {
  const result = await store.request(
    queryCoachingSharedResourcesByPartnership(coachingPartnershipId)
  );
  const shares = result.content.data ?? [];

  return Promise.all(
    shares.map(async (share) => ({
      share,
      resource: await resolveShareResource(share),
    }))
  );
}

/**
 * Duplicate partnership+resource shares are 409 from ll-api ("Already shared").
 */
export function isAlreadySharedError(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const status = (error as { response?: { status?: number } }).response?.status;
  return status === 409;
}
